import { requestInvoice, requestTickets, setCustomer } from "@/redux/ducks/customer";
import { requestInvoice as requestUserInvoice, requestTickets as requestUserTickets, setUser } from "@/redux/ducks/user";
import { ReducersTypes } from "@/redux/reducers/Models";
import { DebtsByInvoice, getFormattedDebtsByInvoice, getFormattedTickets, Tickets } from "@/utils/transform";
import jsPDF from "jspdf";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useI18n from "./useI18n";

export interface Invoice {
    name: string,
    invoiceId: string,
    invoiceDate: string,
    price: string,
    debts: {
        plate: string;
        date: string;
        price: string;
        originalPrice: string;
        fine: string;
        owner: string;
        nmPraca: string,
        fmtDataTransacao: string,
    }[];
}

interface UI {
    isLoading: boolean;
    errorTickets: boolean;
    errorInvoice: boolean;
    isVisibleModal: boolean;
    invoice: Invoice | null;
}

const useTicket = () => {
    const dispatch = useDispatch();
    const customer = useSelector((state: ReducersTypes) => state.customerReducer);
    const user = useSelector((state: ReducersTypes) => state.userReducer);
    const { t } = useI18n();

    const isAuthenticated = !!customer?.client?.id;

    const [ui, setUi] = useState<UI>({
        isLoading: false,
        errorTickets: false,
        errorInvoice: false,
        isVisibleModal: false,
        invoice: null,
    });

    const toggleModal = () => setUi(prev => ({ ...prev, errorInvoice: false, errorTickets: false, isVisibleModal: false }));

    const generatePDF = () => {

        const {
            name,
            invoiceId,
            invoiceDate,
            price,
            debts,
        } = ui.invoice!;

        const tollsPrice = debts?.reduce(
            (sum: number, item) => sum + Number(item?.originalPrice.replace(/,/g, '.')),
            0
        );
        const fines = debts?.reduce(
            (sum: number, item) => sum + Number(item?.fine.replace(/,/g, '.')),
            0
        );

        const doc = new jsPDF();

        const pageHeight = doc.internal.pageSize.height;
        const pageWidth = doc.internal.pageSize.width;
        const marginTop = 20;
        let currentY = 20;

        const logo = "/pdfimage.png";
        doc.addImage(logo, "PNG", 90, marginTop, 42, 14);

        currentY = marginTop + 30;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(t("pages:receipt:confirmationOfPaymentOfTicket"), pageWidth / 2, currentY, { align: "center" });

        currentY += 15;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(t("pages:receipt:hello") + name, 20, currentY);

        currentY += 8;
        doc.text(t("pages:receipt:yourPaymentOf"), 20, currentY);
        doc.setFont("helvetica", "bold");
        doc.text("R$ " + price, 20 + doc.getTextWidth(t("pages:receipt:yourPaymentOf")), currentY);
        doc.setFont("helvetica", "normal");
        doc.text(t("pages:receipt:wasSuccessfully"), 20 + doc.getTextWidth(t("pages:receipt:yourPaymentOf") + "R$ " + price + "  "), currentY);

        currentY += 15;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(t("pages:receipt:transactionData") + "#" + invoiceId, 20, currentY);

        currentY += 10;

        const addDataRow = (label: string, value: string, isLast = false) => {
            const rowHeight = 8;
            const startX = 20;
            const endX = pageWidth - 20;

            checkPageBreak(rowHeight);

            doc.rect(startX, currentY - 2, endX - startX, rowHeight);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(label, startX + 2, currentY + 3);
            doc.text(value, endX - 2, currentY + 3, { align: "right" });

            currentY += rowHeight;
        };

        const checkPageBreak = (requiredSpace: number) => {
            const bottomMargin = 30;
            if (currentY + requiredSpace > pageHeight - bottomMargin) {
                doc.addPage();
                currentY = marginTop;
                return true;
            }
            return false;
        };

        if (debts && debts.length > 0) {
            debts.forEach((debt, index) => {

                const requiredSpace = 5 * 8 + 10;
                checkPageBreak(requiredSpace);

                const rowHeight = 8;
                const startX = 20;
                const endX = pageWidth - 20;


                doc.rect(startX, currentY - 2, endX - startX, rowHeight);
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.text(t("pages:receipt:plate"), startX + 2, currentY + 3);
                doc.setFont("helvetica", "bold");
                doc.text(debt.plate || "N/A", endX - 2, currentY + 3, { align: "right" });
                currentY += rowHeight;

                addDataRow(t("pages:receipt:date"), debt.fmtDataTransacao || invoiceDate);
                addDataRow(t("pages:receipt:concessionary"), debt.owner || "N/A");
                addDataRow(t("pages:receipt:port"), debt.nmPraca || "N/A");

                const originalPrice = Number(debt.originalPrice?.replace(/,/g, '.') || 0);

                addDataRow(t("pages:receipt:value"), "R$ " + originalPrice.toFixed(2).replace(/\./g, ','));

                if (index < debts.length - 1) {
                    currentY += 5;
                }
            });
        }

        currentY += 10;

        checkPageBreak(50);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(t("pages:receipt:detailsOfPayment"), 20, currentY);

        currentY += 10;

        addDataRow(t("pages:receipt:toll"), "R$ " + tollsPrice?.toFixed(2)?.replace(/\./g, ','));
        addDataRow(t("pages:receipt:fines"), "R$ " + fines?.toFixed(2)?.replace(/\./g, ','));

        const rowHeight = 8;
        const startX = 20;
        const endX = pageWidth - 20;

        doc.rect(startX, currentY - 2, endX - startX, rowHeight);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(t("pages:receipt:total"), startX + 2, currentY + 3);
        doc.text("R$ " + price, endX - 2, currentY + 3, { align: "right" });

        currentY += rowHeight;

        currentY = pageHeight - 15;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(t("pages:receipt:reportGeneratedIn") + new Date().toLocaleDateString("pt-BR"), pageWidth / 2, currentY, { align: "center" });

        doc.save(invoiceId + ".pdf");

        const data = doc.output("bloburi");

        if ((window as any).ReactNativeWebView) {
            (window as any).ReactNativeWebView.postMessage(JSON.stringify({
                type: 'download-pdf',
                data, 
            }));
        }

        return;
    };

    const handleRequestInvoice = useCallback((invoice: {
        date: string;
        price: string;
        id: string;
        status: string;
    }, success: boolean, code?: string | DebtsByInvoice[]) => {
        setUi(prev => ({ ...prev, isLoading: false }));

        if (!success) {
            setUi(prev => ({ ...prev, errorInvoice: false }));
            return;
        }

        setUi(prev => ({
            ...prev,
            isVisibleModal: true,
            invoice: {
                name: isAuthenticated ? customer?.client?.name! : "Usuário",
                invoiceId: invoice.id,
                invoiceDate: invoice.date,
                price: invoice.price,
                debts: getFormattedDebtsByInvoice(code! as DebtsByInvoice[]),
            }
        }));
    }, [customer, isAuthenticated]);

    const getInvoice = useCallback((invoice: {
        date: string;
        price: string;
        id: string;
        status: string;
    }) => {
        if (ui.isLoading) return;

        setUi(prev => ({ ...prev, isLoading: true, errorTickets: false, errorInvoice: false }));

        if (isAuthenticated) {
            dispatch(
                requestInvoice({
                    invoiceId: invoice?.id,
                    callback: (success, code) => handleRequestInvoice(invoice, success, code),
                })
            );
        } else {
            dispatch(
                requestUserInvoice({
                    invoiceId: invoice?.id,
                    callback: (success, code) => handleRequestInvoice(invoice, success, code),
                })
            );
        }
    }, [dispatch, ui.isLoading, handleRequestInvoice, customer, isAuthenticated, user?.accessToken]);

    const handleRequestTickets = useCallback(
        ({ success, code, callback }: { success: boolean; code?: string | Tickets[]; callback?: () => void }) => {
            setUi(prev => ({ ...prev, isLoading: false }));

            if (!success) {
                setUi(prev => ({ ...prev, errorTickets: false }));
                if (isAuthenticated) {
                    dispatch(
                        setCustomer({
                            tickets: [],
                        })
                    );
                } else {
                    dispatch(
                        setUser({
                            tickets: [],
                        })
                    );
                }
                callback?.();
                return;
            }

            const formattedTickets = getFormattedTickets(code! as Tickets[]);

            if (isAuthenticated) {
                dispatch(
                    setCustomer({
                        tickets: formattedTickets,
                    })
                );
            } else {
                dispatch(
                    setUser({
                        tickets: formattedTickets,
                    })
                );
            }

            callback?.();
        },
        [dispatch, isAuthenticated]
    );

    const getTickets = useCallback((callback?: () => void) => {
        if (ui.isLoading) return;

        setUi(prev => ({ ...prev, isLoading: true, errorTickets: false, errorInvoice: false }));

        if (isAuthenticated) {
            dispatch(
                requestTickets({
                    callback: (success, code) => handleRequestTickets({
                        success,
                        code,
                        callback,
                    }),
                })
            );
        } else {
            dispatch(
                requestUserTickets({
                    callback: (success, code) => handleRequestTickets({
                        success,
                        code,
                        callback,
                    }),
                })
            );
        }
    }, [dispatch, handleRequestTickets, ui.isLoading, isAuthenticated, user?.accessToken]);

    return {
        toggleModal,
        getInvoice,
        getTickets,
        generatePDF,
        customer,
        user,
        isAuthenticated,
        tickets: isAuthenticated ? customer?.tickets : user?.tickets,
        ...ui,
    };
};

export default useTicket;